import { useCallback, useEffect, useMemo, useState } from 'react'
import { Timestamp, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import type { Change, DeclarationDoc, DriverChange } from '@racedash/shared'
import { db } from '../lib/firebase'

/**
 * What this organisation says about who was in which car.
 *
 * An overlay, not a shared fact: it lives under the organisation's own id, two
 * teams may hold different beliefs about the same car, and neither has to claim
 * ownership of it. The read side has existed since the timing table needed to
 * show the distinction between a team's answer and the vendor's; this is the
 * write side, which had nowhere to live until the stint chart.
 *
 * Append-only. A correction is a later entry rather than an edit, because the
 * question being answered is "who was in it at 14:20" and rewriting history
 * would make that unanswerable — which is the whole reason this is a time
 * series rather than a current value.
 */
export function useDeclarations(streamSessionId: string | null, orgId: string | undefined) {
  const [changes, setChanges] = useState<Change[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!streamSessionId || !orgId) {
      setChanges([])
      return
    }
    return onSnapshot(
      doc(db, 'streamSessions', streamSessionId, 'declarations', orgId),
      (snap) => {
        const d = snap.data() as DeclarationDoc | undefined
        setChanges(
          (d?.changes ?? []).map((c) => ({
            car: c.car,
            driverId: c.driverId,
            from: c.from.toDate(),
          })),
        )
      },
      // Never folded into an empty list. "Nobody has declared anything" and "we
      // were not allowed to ask" look identical otherwise, and only one of them
      // means the driver names on screen are the vendor's by choice.
      (e) => setError(e.message),
    )
  }, [streamSessionId, orgId])

  /**
   * Record that a driver got into a car at a moment.
   *
   * The moment is passed in rather than taken as "now", because the natural act
   * on a stint chart is "that stint was driven by her" — said afterwards, about
   * a stint that started an hour ago. Stamping it with the current time would
   * put the change at the wrong end of the stint and leave the vendor's answer
   * standing for the part anybody was actually asking about.
   */
  const declare = useCallback(
    async (car: string, driverId: string | null, from: Date) => {
      if (!streamSessionId || !orgId) return
      const entry: DriverChange = { car, driverId, from: Timestamp.fromDate(from) }
      // Replaces any change this organisation already made for the same car at
      // the same instant — declaring twice about one stint is a correction, not
      // a second stint — and appends otherwise.
      const next = [
        ...changes
          .filter((c) => !(c.car === car && c.from.getTime() === from.getTime()))
          .map((c) => ({ car: c.car, driverId: c.driverId, from: Timestamp.fromDate(c.from) })),
        entry,
      ]
      await setDoc(
        doc(db, 'streamSessions', streamSessionId, 'declarations', orgId),
        { orgId, changes: next, updatedAt: serverTimestamp() },
        { merge: true },
      )
    },
    [streamSessionId, orgId, changes],
  )

  return useMemo(() => ({ changes, declare, error }), [changes, declare, error])
}
